/**
 * 字体加载优化工具类
 * 提供字体预加载、加载状态检测等功能
 */

interface FontConfig {
  family: string
  url: string
  format: string
  display?: 'auto' | 'block' | 'swap' | 'fallback' | 'optional'
}

class FontLoader {
  private loadedFonts = new Set<string>()
  private loadingPromises = new Map<string, Promise<void>>()

  /**
   * 预加载字体文件
   * @param fonts 字体配置数组
   */
  async preloadFonts(fonts: FontConfig[]): Promise<void> {
    const promises = fonts.map((font) => this.loadFont(font))
    await Promise.allSettled(promises)
  }

  /**
   * 加载单个字体
   * @param font 字体配置
   */
  async loadFont(font: FontConfig): Promise<void> {
    const fontKey = `${font.family}-${font.url}`

    if (this.loadedFonts.has(fontKey)) {
      return Promise.resolve()
    }

    if (this.loadingPromises.has(fontKey)) {
      return this.loadingPromises.get(fontKey)!
    }

    const promise = this.performFontLoad(font, fontKey)
    this.loadingPromises.set(fontKey, promise)

    return promise
  }

  private async performFontLoad(font: FontConfig, fontKey: string): Promise<void> {
    try {
      // 使用 Font Loading API
      if ('FontFace' in window) {
        const fontFace = new FontFace(font.family, `url(${font.url}) format('${font.format}')`)

        if (font.display) {
          fontFace.display = font.display
        }

        await fontFace.load()
        document.fonts.add(fontFace)
        this.loadedFonts.add(fontKey)
      } else {
        // 降级方案：使用预加载链接
        await this.fallbackLoad(font.url)
        this.loadedFonts.add(fontKey)
      }
    } catch (error) {
      console.warn(`字体加载失败: ${font.family}`, error)
      // 不抛出错误，允许使用系统字体降级
    } finally {
      this.loadingPromises.delete(fontKey)
    }
  }

  private fallbackLoad(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const link = document.createElement('link')
      link.rel = 'preload'
      link.as = 'font'
      link.crossOrigin = 'anonymous'
      link.href = url

      link.onload = () => resolve()
      link.onerror = () => reject(new Error(`Failed to load ${url}`))

      document.head.appendChild(link)
    })
  }

  /**
   * 检查字体是否已加载
   * @param fontFamily 字体族名称
   */
  isFontLoaded(fontFamily: string): boolean {
    if ('fonts' in document) {
      return document.fonts.check(`1em ${fontFamily}`)
    }
    return false
  }

  /**
   * 等待字体加载完成
   * @param fontFamily 字体族名称
   * @param timeout 超时时间（毫秒）
   */
  async waitForFont(fontFamily: string, timeout = 3000): Promise<boolean> {
    if (this.isFontLoaded(fontFamily)) {
      return true
    }

    const startTime = Date.now()

    return new Promise((resolve) => {
      const checkFont = () => {
        if (this.isFontLoaded(fontFamily)) {
          resolve(true)
        } else if (Date.now() - startTime >= timeout) {
          resolve(false)
        } else {
          requestAnimationFrame(checkFont)
        }
      }

      checkFont()
    })
  }

  /**
   * 获取所有已加载的字体
   */
  getLoadedFonts(): string[] {
    return Array.from(this.loadedFonts)
  }
}

// 创建全局实例
export const fontLoader = new FontLoader()

// 项目中使用的字体配置
// 注意：在生产环境中，这些URL会被Vite自动处理为正确的hash文件名
export const PROJECT_FONTS: FontConfig[] = [
  {
    family: 'Vuestic Icons',
    url: '/src/styles/icon-fonts/vuestic-icons/vuestic-icons.woff',
    format: 'woff',
    display: 'swap',
  },
  {
    family: 'Material Symbols Outlined',
    url: '/src/styles/icon-fonts/material-symbols-outlined-icons/MaterialSymbolsOutlined.woff2',
    format: 'woff2',
    display: 'swap',
  },
]

export default fontLoader
